import type { NavGroup } from '@core/layouts/types'

/**
 * Given the sidebar nav structure and the current open/closed map, returns the
 * key of a closed group that contains an item matching the active pathname —
 * or `null` if no such group needs to be opened.
 *
 * Used by the sidebar to auto-expand the parent section when the user lands on
 * a page via Cmd+K (Quick Search), a `G`-leader keyboard shortcut, an address-
 * bar paste, or any other route change that bypasses an explicit nav-item
 * click. Mouse-click navigation already opens the group as a side-effect of
 * the click itself, so this helper is effectively a no-op for that path.
 *
 * Match shape — uses `pathname.startsWith(item.path)` rather than the codebase's
 * `pathname === item.path || pathname.startsWith(\`${item.path}/\`)` pattern
 * (see `VerticalNavChildRow.tsx:43`). The codebase pattern is designed for
 * items WITHOUT trailing slashes — but our `routeDirectory` (via `makeRoute`)
 * guarantees every NavLink path ends in `/`, and `next.config.trailingSlash`
 * is `true`, so `pathname` also ends in `/`. With both forms aligned,
 * `startsWith(item.path)` correctly matches:
 *   - `/chargers/` (list page) → `/chargers/` ✓
 *   - `/chargers/cy123/` (detail page) → `/chargers/` ✓
 *   - `/chargers-old/` (sibling) → `/chargers/` ✗ (rejected — no shared prefix)
 *
 * Returns `null` (not an empty string or undefined) when:
 *   - the pathname has no NavLink anywhere in the nav tree, OR
 *   - every group containing a match is already open.
 *
 * Strictly read-only — never mutates inputs. Callers are responsible for
 * dispatching the actual `setOpenGroups` update.
 */
export default function findClosedGroupForPath(
  groups: NavGroup[],
  openGroups: Record<string, boolean>,
  pathname: string | null | undefined
): string | null {
  if (!pathname) return null

  for (const group of groups) {
    if (openGroups[group.key]) continue
    const hasMatch = group.items.some(
      // Skip empty paths AND a root `/` path: `pathname.startsWith('/')` is
      // true for every web pathname, so a root NavLink would falsely match
      // every page and indiscriminately open its group. No such NavLink
      // exists today; this is a defensive guard against a future regression.
      (item) =>
        typeof item.path === 'string' && item.path.length > 0 && item.path !== '/' && pathname.startsWith(item.path)
    )
    if (hasMatch) return group.key
  }

  return null
}
